/**
 * Moon Query Router — Routes lunar intents to handlers for Moon globe.
 * Handles navigation, landing sites, named features, filtering, and educational responses.
 */
import {
  findLunarLocation,
  getLandingSites,
  getNamedFeatures,
} from '../data/lunar-data.js';
import { getMoonMap } from '../globe/moon-globe.js';

class MoonQueryRouter {
  constructor({ speechOutput, moonMarkers }) {
    this.speech = speechOutput;
    this.moonMarkers = moonMarkers; // { addMarkers, clearMarkers, addPopup }
    this._lastResultCount = null;
  }

  /**
   * Route a parsed lunar intent.
   */
  async route(intent) {
    this._lastResultCount = null;

    try {
      switch (intent.type) {
        case 'lunar_navigation':
          await this._handleNavigation(intent);
          break;
        case 'lunar_landing':
          await this._handleLandingSites(intent);
          break;
        case 'lunar_feature':
          await this._handleFeature(intent);
          break;
        case 'lunar_filter':
          await this._handleFilter(intent);
          break;
        case 'lunar_educational':
          // Voice response only — no map action
          break;
        default:
          // Try navigation as fallback
          await this._handleNavigation(intent);
      }

      // Speak response
      if (intent.voiceResponse?.shouldSpeak && intent.voiceResponse?.template) {
        const text = this._interpolate(intent.voiceResponse.template);
        this.speech.speak(text);
      }
    } catch (err) {
      console.error('[MoonQueryRouter] Error:', err);
      throw err;
    }
  }

  /**
   * Navigate to a lunar location.
   */
  async _handleNavigation(intent) {
    const moonMap = getMoonMap();
    if (!moonMap) return;

    const target = intent.target;
    let location = null;

    if (target?.coordinates) {
      location = {
        center: target.coordinates,
        zoom: target.type === 'mare' || target.type === 'basin' ? 3 : 6,
      };
    } else if (target?.name) {
      location = findLunarLocation(target.name);
    }

    if (location) {
      moonMap.flyTo({
        center: location.center,
        zoom: location.zoom || 5,
        duration: 2000,
        essential: true,
      });

      // Show marker if navigating to a specific feature
      if (location.feature) {
        this.moonMarkers.clearMarkers();
        this.moonMarkers.addMarkers([location.feature]);
      }
    }
  }

  /**
   * Show all landing sites.
   */
  async _handleLandingSites(intent) {
    const moonMap = getMoonMap();
    if (!moonMap) return;

    const sites = getLandingSites();
    this._lastResultCount = sites.length;

    this.moonMarkers.clearMarkers();
    this.moonMarkers.addMarkers(sites, 'landing');

    // Zoom out to show all
    moonMap.flyTo({
      center: [0, 0],
      zoom: 1.5,
      duration: 2000,
      essential: true,
    });
  }

  /**
   * Show a specific named feature.
   */
  async _handleFeature(intent) {
    const moonMap = getMoonMap();
    if (!moonMap) return;

    const target = intent.target;
    const location = findLunarLocation(target?.name || '');

    if (location) {
      moonMap.flyTo({
        center: location.center,
        zoom: location.zoom || 5,
        duration: 2000,
        essential: true,
      });

      if (location.feature) {
        this.moonMarkers.clearMarkers();
        this.moonMarkers.addMarkers([location.feature]);
      }
    }
  }

  /**
   * Filter landing sites or features by attribute.
   */
  async _handleFilter(intent) {
    const moonMap = getMoonMap();
    if (!moonMap) return;

    const targetType = intent.target?.type;
    const filter = intent.operation?.filter;

    let features = [];

    if (targetType === 'landing_site' || !targetType) {
      // Filter landing sites
      if (filter) {
        const attr = filter.attribute;
        const val = filter.value;

        if (attr === 'country_year') {
          const [country, year] = val.split('_');
          features = getLandingSites({ country_year: val });
        } else if (attr === 'country') {
          features = getLandingSites({ country: val });
        } else if (attr === 'category') {
          features = getLandingSites({ category: val });
        } else if (attr === 'mission') {
          // contains match
          features = getLandingSites().filter(f =>
            f.properties.mission.toLowerCase().includes(val.toLowerCase())
          );
        } else if (attr === 'year') {
          const op = filter.operator;
          const numVal = parseInt(val);
          features = getLandingSites().filter(f => {
            const y = f.properties.year;
            if (op === '==') return y === numVal;
            if (op === '>') return y > numVal;
            if (op === '<') return y < numVal;
            if (op === '>=') return y >= numVal;
            if (op === '<=') return y <= numVal;
            return true;
          });
        } else if (attr === 'agency') {
          features = getLandingSites({ agency: val });
        } else {
          features = getLandingSites();
        }
      } else {
        features = getLandingSites();
      }

      this._lastResultCount = features.length;
      this.moonMarkers.clearMarkers();
      this.moonMarkers.addMarkers(features, 'landing');
    } else {
      // Filter named features by type
      features = getNamedFeatures(targetType);
      this._lastResultCount = features.length;
      this.moonMarkers.clearMarkers();
      this.moonMarkers.addMarkers(features, 'feature');
    }

    // Zoom to fit all results or zoom out
    if (features.length === 1) {
      const [lng, lat] = features[0].geometry.coordinates;
      moonMap.flyTo({ center: [lng, lat], zoom: 6, duration: 2000, essential: true });
    } else if (features.length > 0) {
      moonMap.flyTo({ center: [0, 0], zoom: 1.5, duration: 2000, essential: true });
    }
  }

  /**
   * Interpolate {count} and other placeholders.
   */
  _interpolate(template) {
    return template.replace(/\{count\}/g, this._lastResultCount || '');
  }

  /**
   * Get the interpolated description for UI display.
   */
  interpolateResponse(template) {
    return this._interpolate(template);
  }
}

export default MoonQueryRouter;
